import { acquisitionAttribution } from "./acquisitionAttribution.js";
import { attributionConsent } from "./consent.js";
import { mountTrafficConsent } from "./trafficConsentCard.js";
import { createAttributionNavigation, removeAttributionParameter } from "./attributionNavigation.js";

export function createAttributionController({
  window = globalThis.window, document = globalThis.document,
  service = acquisitionAttribution, consent = attributionConsent,
  isAuthenticated = () => false,
} = {}) {
  let captured = false;
  let disposed = false;
  let suspended = false;
  let channel;
  let navigation;
  const listeners = new Set();

  function getAttribution() {
    try {
      return !disposed && !suspended && consent.hasConsent() && !isAuthenticated()
        ? (service.recover() || null) : null;
    } catch { return null; }
  }

  function notify() {
    for (const listener of listeners) listener();
  }

  function capture() {
    if (disposed || captured || suspended) return;
    try {
      if (!consent.hasConsent() || isAuthenticated()) return;
      captured = true;
      // Never retain incoming evidence in memory before consent.
      const handoff = service.readHandoff();
      let navigationType;
      try { navigationType = window.performance?.getEntriesByType("navigation")[0]?.type; } catch { /* Optional hint. */ }
      const restoredVisit = ["reload", "back_forward"].includes(navigationType);
      const reuseHistory = restoredVisit && service.recover(handoff);
      service.prepare({ consentGranted: true, handoff, capture: !reuseHistory });
      removeAttributionParameter(window);
      navigation?.decorate();
      notify();
    } catch { /* Attribution must not interrupt application bootstrap. */ }
  }

  function clear(reason = "signup", broadcast = true) {
    captured = false;
    suspended = true;
    try { service.clear(reason); } catch { /* Continue cleanup. */ }
    navigation?.restore();
    removeAttributionParameter(window);
    notify();
    if (broadcast) {
      try { channel?.postMessage({ type: "clear", reason }); } catch { /* Best effort. */ }
    }
  }

  const unsubscribe = consent.subscribe((granted) => {
    if (!granted) clear("withdrawal");
    else { suspended = false; capture(); }
  });

  capture();
  navigation = createAttributionNavigation({ window, document, getAttribution });
  try {
    channel = new window.BroadcastChannel("ap-attribution-v1");
    channel.onmessage = (event) => {
      if (event.data?.type !== "clear" || !["signup", "withdrawal"].includes(event.data.reason)) return;
      clear(event.data.reason, false);
    };
  } catch { /* Storage/navigation still work without cross-tab messaging. */ }
  const onPageShow = (event) => {
    if (event.persisted) {
      // A BFCache restore must not resurrect an already-completed/withdrawn snapshot.
      try {
        if (consent.hasConsent() && !suspended) service.recover();
      } catch { /* Optional recovery. */ }
      navigation.decorate();
      notify();
    }
  };
  window.addEventListener("pageshow", onPageShow);
  const onRefresh = () => {
    if (disposed || suspended || !consent.hasConsent() || isAuthenticated()) return;
    service.recover();
    navigation.decorate();
    notify();
  };
  window.addEventListener("storage", onRefresh);
  window.addEventListener("focus", onRefresh);

  return {
    getAttribution, capture, clear,
    destination: (href) => navigation.destination(href),
    decorateLinks: () => navigation.decorate(),
    subscribe(listener) { listeners.add(listener); return () => listeners.delete(listener); },
    dispose() {
      disposed = true;
      unsubscribe();
      channel?.close();
      navigation.dispose();
      window.removeEventListener("pageshow", onPageShow);
      window.removeEventListener("storage", onRefresh);
      window.removeEventListener("focus", onRefresh);
      listeners.clear();
    },
  };
}

let controller = null;
export function startAppAttribution(options = {}) {
  if (!controller) {
    try { controller = createAttributionController(options); } catch { /* Never block app bootstrap. */ }
  }
  try {
    const target = options.window || globalThis.window;
    const ui = mountTrafficConsent({ window: target, document: options.document || target?.document, consent: options.consent || attributionConsent, isAuthenticated: options.isAuthenticated, subscribeSession: options.subscribeSession });
    if (target && ui) target.APTrafficConsent = { open: ui.open, refresh: ui.refresh };
  } catch { /* A preference UI failure must not prevent authentication. */ }
  return controller;
}
export function getAppAttribution() { return controller; }
export function attributionRedirect(href) { return controller?.destination(href) || href; }
export function clearAppAttribution() {
  if (controller) controller.clear();
  else acquisitionAttribution.clear();
}
